import * as _ from "lodash";

import { GenerateSimulationResponse } from "@models/simulations/generateSimulationResponse";
import { TradingRepo } from "@services/trading.repo";
import { TradeRestriction } from "@models/trading/tradeRestriction";
import { TradeSimulation } from "@models/simulations/tradeSimulation";
import { SecurityDetails } from "@models/simulations/securityDetails";
import { ExchangeService } from "@common/services/exchange.service";
import { ExternalSourceName } from "@common/enums/ExternalSources";
import { FixedIncomeTypeNames } from "@common/enums/AssetClassType";

class SecuritySimilationController implements angular.IOnChanges {
  public clientSimulations: GenerateSimulationResponse;
  public clients: string[];
  public securityDetails: SecurityDetails;
  public assetClass: string;

  public loading: boolean = false;
  public totalBuy: number = 0;
  public totalSell: number = 0;
  public totalBuyQty: number = 0;
  public totalSellQty: number = 0;

  /** @ngInject */
  constructor(
    private $rootScope: angular.IRootScopeService,
    private $mdDialog: angular.material.IDialogService,
    private $window: angular.IWindowService,
    private tradingRepo: TradingRepo,
    private exchangeService: ExchangeService,
    private notifyjs: any
  ) {}

  $onChanges(onChangesObj: angular.IOnChangesObject): void {
    if (!onChangesObj.clientSimulations) {
      this.totalBuy = 0;
      this.totalSell = 0;
      this.totalBuyQty = 0;
      this.totalSellQty = 0;
    }
  }

  showCashLimitDetails(portfolioSimulation: TradeSimulation) {
    if (!portfolioSimulation.cashLimits) {
      return;
    }

    return `Cash should be between ${portfolioSimulation.cashLimits.lowLimit.toFixed(
      2
    )}% and ${portfolioSimulation.cashLimits.highLimit.toFixed(
      2
    )}% of portfolio.
Currently at ${(
      (portfolioSimulation.cashAvailable * 100) /
      portfolioSimulation.portfolioNAV
    ).toFixed(2)}%`;
  }

  showAssetLimitDetails(portfolioSimulation: TradeSimulation) {
    if (!portfolioSimulation.assetLimits) {
      return;
    }

    return `${
      portfolioSimulation.assetLimits.assetClass
    } should be between ${portfolioSimulation.assetLimits.lowLimit.toFixed(
      2
    )}% and ${portfolioSimulation.assetLimits.highLimit.toFixed(
      2
    )}% of portfolio.
Currently at ${(
      (portfolioSimulation.targetAssetClassNav * 100) /
      portfolioSimulation.portfolioNAV
    ).toFixed(2)}%`;
  }

  showRestrictionDetails(tradeRestriction: TradeRestriction) {
    if (!tradeRestriction) {
      return;
    }

    return `Active: ${tradeRestriction.enabled ? "Yes" : "No"}
Restriction: ${tradeRestriction.restriction()}
Type: ${tradeRestriction.type()}
Created By: ${
      tradeRestriction.createdBy ? tradeRestriction.createdBy.name : "Unknown"
    }
Date Created: ${new Date(tradeRestriction.dateCreated)}`;
  }

  showActiveTradeDetails(securityId: number, portfolioId: number) {
    this.tradingRepo
      .getActiveTradeDetails(securityId, portfolioId)
      .then((resp) => {
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.tradeDetails = resp.data;

        return this.$mdDialog.show({
          template:
            '<trade-details-modal trade-details="tradeDetails"></trade-details-modal>',
          scope: tempScope,
        });
      });
  }

  checkSecurityTrades(securityId: number, portfolio) {
    let returnValue = false;
    let totalTradeQuantity = 0;
    this.tradingRepo
      .getActiveTradeDetails(securityId, portfolio.portfolioId)
      .then((resp) => {
        let trades = resp.data as any;
        trades.activeTrades.forEach((element) => {
          if (element.quantityRecovered !== Math.abs(element.buySellQuantity)) {
            if (element.buySellQuantity < 0) {
              totalTradeQuantity += Math.abs(element.buySellQuantity);
            }
          }
        });
        if (portfolio.buySellQuantity < 0) {
          if (totalTradeQuantity > 0) {
            if (
              Math.abs(portfolio.quantity) - Math.abs(totalTradeQuantity) ==
              0
            ) {
              returnValue = true;
            } else if (
              Math.abs(portfolio.quantity) - Math.abs(totalTradeQuantity) <=
              Math.abs(portfolio.buySellQuantity)
            ) {
              returnValue = true;
            }
          }
        }
        portfolio.sellAllowed = returnValue;
      });
  }

  matchIdealBuySell(portfolioSimulation: TradeSimulation) {
    const { restriction, idealBuySell } = portfolioSimulation;
    if (restriction && restriction.enabled) {
      if (restriction.noBuy && restriction.noSell) {
        return;
      }

      if (restriction.noBuy && idealBuySell > 0) {
        return;
      }

      if (restriction.noSell && idealBuySell < 0) {
        return;
      }

      if (restriction.percentageOfPortfolio && !restriction.assetClass) {
        if (
          portfolioSimulation.targetPortfolioPercentage >
          restriction.maxPortfolio
        ) {
          portfolioSimulation.buySellQuantity = this.getQuantityFromDifference(
            restriction.maxPortfolio,
            portfolioSimulation
          );
        } else if (
          portfolioSimulation.targetPortfolioPercentage <
          restriction.minPortfolio
        ) {
          portfolioSimulation.buySellQuantity = this.getQuantityFromDifference(
            restriction.minPortfolio,
            portfolioSimulation
          );
        } else {
          portfolioSimulation.buySellQuantity = idealBuySell;
        }
      } else if (restriction.assetClass) {
        portfolioSimulation.buySellQuantity = idealBuySell;
      }
    } else {
      portfolioSimulation.buySellQuantity = idealBuySell;
    }
    this.updateCashBalance(portfolioSimulation);
  }

  private getQuantityFromDifference(
    percentage: number,
    portfolioSimulation: TradeSimulation
  ) {
    const percentageDifference =
      percentage - portfolioSimulation.portfolioPercentage;
    const percentageAsDecimal = percentageDifference / 100;
    const marketValue = portfolioSimulation.portfolioNAV * percentageAsDecimal;
    return marketValue / portfolioSimulation.targetPrice;
  }

  updateCashBalance(portfolioSimulation: TradeSimulation) {
    if (portfolioSimulation.restriction != null && portfolioSimulation.restriction.enabled) {
      if (portfolioSimulation.restriction.noBuy == true && portfolioSimulation.buySellQuantity > 0) {
        this.notifyjs("Cannot apply positive numbers, portfolio has buy restriction in place!");
        portfolioSimulation.buySellQuantity = 0;
        return;
      }
      if (portfolioSimulation.restriction.noSell == true && portfolioSimulation.buySellQuantity < 0) {
        this.notifyjs("Cannot apply negative numbers, portfolio has sell restriction in place!");
        portfolioSimulation.buySellQuantity = 0;
        return;
      }
    }
    portfolioSimulation.targetQuantity =
      portfolioSimulation.buySellQuantity + portfolioSimulation.quantity;
    portfolioSimulation.tradeValue =
      portfolioSimulation.buySellQuantity * portfolioSimulation.targetPrice;
    portfolioSimulation.targetMarketValue =
      portfolioSimulation.targetQuantity * portfolioSimulation.targetPrice;
    if (this.assetClass === FixedIncomeTypeNames.BOND) {
      portfolioSimulation.tradeValue =
        (portfolioSimulation.buySellQuantity *
          portfolioSimulation.targetPrice) /
        100;
      portfolioSimulation.targetMarketValue =
        (portfolioSimulation.targetQuantity * portfolioSimulation.targetPrice) /
        100;
    }

    portfolioSimulation.targetPortfolioPercentage =
      (portfolioSimulation.targetMarketValue * 100) /
      portfolioSimulation.portfolioNAV;
    portfolioSimulation.targetAssetClassPercentage =
      (portfolioSimulation.targetMarketValue * 100) /
      portfolioSimulation.assetClassNav;

    const buyTrades = _.mapValues(this.clientSimulations, (s) =>
      this.getPortfolioTradeValueTotals(s, (p) => p > 0)
    );
    const sellTrades = _.mapValues(this.clientSimulations, (s) =>
      this.getPortfolioTradeValueTotals(s, (p) => p < 0)
    );

    this.totalBuy = _.reduce(buyTrades, this.tradeValueAccummulator, 0);
    this.totalSell = _.reduce(sellTrades, this.tradeValueAccummulator, 0);

    const buyQuantites = _.mapValues(this.clientSimulations, (s) =>
      this.getPortfolioQuantityTotals(s, (p) => p > 0)
    );
    const sellQuantites = _.mapValues(this.clientSimulations, (s) =>
      this.getPortfolioQuantityTotals(s, (p) => p < 0)
    );

    this.totalBuyQty = _.reduce(buyQuantites, this.quantityAccummulator, 0);
    this.totalSellQty = _.reduce(sellQuantites, this.quantityAccummulator, 0);

    this.checkSecurityTrades(this.securityDetails.id, portfolioSimulation);
  }

  showCashAccountBalances(portfolioSimulation: TradeSimulation) {
    let dialogScope = this.$rootScope.$new() as any;
    dialogScope.cashAccounts = portfolioSimulation.currencyBalances;
    dialogScope.buySellDetails = {
      [portfolioSimulation.portfolioCurrency]: portfolioSimulation.tradeValue,
    };

    this.$mdDialog.show({
      template:
        '<cash-balance-dialog cash-accounts="cashAccounts" buy-sell-details="buySellDetails"></cash-balance-dialog>',
      scope: dialogScope,
    });
  }

  cashBalanceWarning(portfolioSimulation: TradeSimulation) {
    return `The ${portfolioSimulation.portfolioCurrency} cash account does not have enough funds for this trade.`;
  }

  private getPortfolioTradeValueTotals(
    portfolios: TradeSimulation[],
    filterFunc
  ) {
    return portfolios
      .map((p) => p.tradeValue)
      .filter(filterFunc)
      .reduce(this.tradeValueAccummulator, 0);
  }

  private tradeValueAccummulator(
    totalTradeValue: number,
    securityTradeValue: number
  ) {
    return totalTradeValue + securityTradeValue;
  }

  private getPortfolioQuantityTotals(
    portfolios: TradeSimulation[],
    filterFunc
  ) {
    return portfolios
      .map((p) => p.buySellQuantity)
      .filter(filterFunc)
      .reduce(this.quantityAccummulator, 0);
  }

  private quantityAccummulator(total: number, currentQuantity: number) {
    return total + currentQuantity;
  }

  canSave() {
    return _.every(this.clientSimulations, (s) =>
      s.every(
        (t) =>
          (t.buySellQuantity === 0 ||
            (t.buySellQuantity !== 0 && t.withinRestriction)) &&
          t.targetQuantity >= 0 &&
          t.sellAllowed != true
      )
    );
  }

  private getTradesForSource(trades: TradeSimulation[], source: string) {
    return trades
      .filter((t) => t.portfolioSource === source)
      .map((t) => ({
        quantity: Math.round(t.buySellQuantity),
        portfolioId: t.portfolioId,
      }));
  }

  private confirmValidity() {
    const hasBuy = _.some(
      this.clientSimulations,
      (s) => !!s.filter((t) => t.buySellQuantity > 0).length
    );
    const hasSell = _.some(
      this.clientSimulations,
      (s) => !!s.filter((t) => t.buySellQuantity < 0).length
    );
    const showUseCase = _.some(
      this.clientSimulations,
      (s) =>
        !!s.filter(
          (t) =>
            t.buySellQuantity !== 0 &&
            t.portfolioSource == ExternalSourceName.EDR
        ).length
    );

    if (!hasBuy && !hasSell) {
      this.notifyjs(
        `Can't generate tradesheet when no trades have been specified`,
        "warning"
      );
      return;
    }
    this.loading = true;

    const tempScope = this.$rootScope.$new() as any;
    tempScope.securityDetails = this.securityDetails;
    tempScope.showBuy = hasBuy;
    tempScope.showSell = hasSell;
    tempScope.locality = this.securityDetails.locality;
    tempScope.showUseCase = showUseCase;
    // console.log(this.securityDetails);

    this.$mdDialog
      .show({
        template: `<simulation-trade-validity security-details="securityDetails" show-buy-validity="showBuy"
      show-sell-validity="showSell" locality="locality" show-use-case="showUseCase"></simulation-trade-validity>`,
        clickOutsideToClose: true,
        scope: tempScope,
      })
      // Potentional Name : createTradeSheetFromSecuritySimulation
      .then((validityDetails) => {
        const allTrades = _.flatMap(this.clientSimulations).filter(
          (t) => !!t.buySellQuantity
        ); // Should filter out null, undefined and 0

        let tradeSheetRequest = [];
        const mcbTrades = this.getTradesForSource(
          allTrades,
          ExternalSourceName.MCB
        );
        if (mcbTrades.length) {
          tradeSheetRequest.push(
            this.tradingRepo.saveSimulationTradeSheet({
              buyValidity: validityDetails.buyValidity,
              sellValidity: validityDetails.sellValidity,
              security: this.securityDetails,
              trades: mcbTrades,
            })
          );
        }

        const edrTrades = this.getTradesForSource(
          allTrades,
          ExternalSourceName.EDR
        );
        if (edrTrades.length) {
          tradeSheetRequest.push(
            this.tradingRepo.saveSimulationTradeSheet({
              buyValidity: validityDetails.buyValidity,
              sellValidity: validityDetails.sellValidity,
              security: this.securityDetails,
              trades: edrTrades,
            })
          );
        }

        return Promise.all(tradeSheetRequest);
      })
      .then((tradeSheetUrls) => {
        tradeSheetUrls.forEach((tradeSheets) => {
          tradeSheets.forEach((tradeSheet) => {
            this.$window.open(tradeSheet.url, "_blank");
          });
        });
        //tradeSheetUrls[0].filter(t => !!t.url).forEach(t => this.$window.open(t.url, '_blank'))
      })
      .catch((err) => this.notifyjs(err))
      .finally(() => (this.loading = false));
  }

  saveTradeSimulation() {
    const cashExceeded = _.reduce(
      this.clientSimulations,
      (prev, curr) => [...prev, ...curr.filter((r) => r.cashAvailable < 0)],
      []
    );

    if (cashExceeded.length) {
      const dialogConfig = this.$mdDialog
        .confirm()
        .title("Cash Exceeeded!")
        .htmlContent(
          `<p>The following portfolio's are exceeding cash balances:</p>
        <ul>${cashExceeded.reduce(
          (prev, curr) => prev + "<li>" + curr.portfolioName + "</li>",
          ""
        )}</ul>
        <p>Would you like to contine?</p>`
        )
        .ok("Yes, continue")
        .cancel("Cancel");

      this.$mdDialog.show(dialogConfig).then(() => this.confirmValidity());
    } else {
      this.confirmValidity();
    }
  }

  checkIfBond() {
    return this.assetClass === FixedIncomeTypeNames.BOND;
  }
}

export const securitySimulation: angular.IComponentOptions = {
  controller: SecuritySimilationController,
  template: require("./securitySimulation.component.html"),
  bindings: {
    clients: "<",
    clientSimulations: "<",
    securityDetails: "<",
    assetClass: "<",
  },
};



// WEBPACK FOOTER //
// ./src/app/simulations/components/securitySimulation.component.ts